// src/services/roles.ts
import { supabase } from "../lib/supabase";
import type { ID, UserRole, CompanyMember, ProjectMember, UserProfile } from "../types/domain";

// Company Members Management
export async function getCompanyMembers(companyId: ID) {
  const { data, error } = await supabase
    .from('company_members')
    .select(`
      *,
      profiles(*)
    `)
    .eq('company_id', companyId)
    .order('created_at');

  // Transform the data to match the expected structure
  const transformedData = data?.map(item => ({
    ...item,
    user: item.profiles
  })) || null;

  return { data: transformedData as CompanyMember[] | null, error };
}

export async function addCompanyMember(companyId: ID, userId: ID, role: UserRole = "member") {
  const { data, error } = await supabase
    .from('company_members')
    .insert({
      company_id: companyId,
      user_id: userId,
      role
    })
    .select(`
      *,
      profiles(*)
    `)
    .single();

  // Transform the data to match the expected structure
  const transformedData = data ? {
    ...data,
    user: data.profiles
  } : null;

  return { data: transformedData as CompanyMember | null, error };
}

export async function updateCompanyMemberRole(memberId: ID, role: UserRole) {
  const { data, error } = await supabase
    .from('company_members')
    .update({ role })
    .eq('id', memberId)
    .select(`
      *,
      profiles(*)
    `)
    .single();

  // Transform the data to match the expected structure
  const transformedData = data ? {
    ...data,
    user: data.profiles
  } : null;

  return { data: transformedData as CompanyMember | null, error };
}

export async function removeCompanyMember(memberId: ID) {
  const { error } = await supabase
    .from('company_members')
    .delete()
    .eq('id', memberId);

  return { error };
}

// Project Members Management
export async function getProjectMembers(projectId: ID) {
  const { data, error } = await supabase
    .from('project_members')
    .select(`
      *,
      profiles(*)
    `)
    .eq('project_id', projectId)
    .order('created_at');

  // Transform the data to match the expected structure
  const transformedData = data?.map(item => ({
    ...item,
    user: item.profiles
  })) || null;

  return { data: transformedData as ProjectMember[] | null, error };
}

export async function addProjectMember(projectId: ID, userId: ID) {
  const { data, error } = await supabase
    .from('project_members')
    .insert({
      project_id: projectId,
      user_id: userId
    })
    .select(`
      *,
      profiles(*)
    `)
    .single();

  // Transform the data to match the expected structure
  const transformedData = data ? {
    ...data,
    user: data.profiles
  } : null;

  return { data: transformedData as ProjectMember | null, error };
}

export async function removeProjectMember(memberId: ID) {
  const { error } = await supabase
    .from('project_members')
    .delete()
    .eq('id', memberId);

  return { error };
}

// User Search and Management
export async function searchUsers(query: string = "") {
  let queryBuilder = supabase
    .from('profiles')
    .select('id, email, full_name, avatar_url, role')
    .order('full_name', { nullsLast: true });

  if (query.trim()) {
    queryBuilder = queryBuilder.or(`
      email.ilike.%${query}%,
      full_name.ilike.%${query}%
    `);
  }

  const { data, error } = await queryBuilder.limit(50);
  return { data: data as UserProfile[] | null, error };
}

export async function getUsersWithoutCompanies(query: string = "") {
  // First get all user IDs that are already in companies
  const { data: companyMembers } = await supabase
    .from('company_members')
    .select('user_id');
  
  const memberUserIds = companyMembers?.map(m => m.user_id) || [];

  let queryBuilder = supabase
    .from('profiles')
    .select('id, email, full_name, avatar_url, role')
    .order('full_name', { nullsLast: true });

  // Exclude users who are already company members
  if (memberUserIds.length > 0) {
    queryBuilder = queryBuilder.not('id', 'in', `(${memberUserIds.map(id => `'${id}'`).join(',')})`);
  }

  if (query.trim()) {
    queryBuilder = queryBuilder.or(`
      email.ilike.%${query}%,
      full_name.ilike.%${query}%
    `);
  }

  const { data, error } = await queryBuilder.limit(50);
  return { data: data as UserProfile[] | null, error };
}

export async function updateUserRole(userId: ID, role: UserRole) {
  const { data, error } = await supabase
    .from('profiles')
    .update({ role })
    .eq('id', userId)
    .select()
    .single();

  return { data: data as UserProfile | null, error };
}

// Get assigned tasks for dashboard notifications
export async function getAssignedTasks(userId: ID) {
  const { data, error } = await supabase
    .from('tasks')
    .select(`
      *,
      project:projects(
        *,
        company:companies(*)
      )
    `)
    .eq('assignee_id', userId)
    .neq('status', 'done')
    .neq('status', 'archived')
    .order('created_at', { ascending: false });

  return { data, error };
}

export async function getRecentlyAssignedTasks(userId: ID, since: string = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()) {
  const { data, error } = await supabase
    .from('tasks')
    .select(`
      *,
      project:projects(
        *,
        company:companies(*)
      )
    `)
    .eq('assignee_id', userId)
    .gte('created_at', since)
    .order('created_at', { ascending: false });

  return { data, error };
}